import React, { useState, useEffect } from 'react';
import { 
  Form, Input, Select, message, Card, Row, Col, 
  InputNumber, Button, Descriptions, Modal, Spin
} from 'antd';
import { PlusOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { orderAPI, dropshipperAPI, exchangeRateAPI, addressAPI, productAPI } from '../api';
import { normalizeMoney } from '../utils/money';
import countryList from 'react-select-country-list';
import { useNavigate, useParams } from 'react-router-dom';
import ProductSearchSelector from '../utils/ProductSearchSelector';

const { Option } = Select;
const { TextArea } = Input;

// 获取API基础URL
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

const EditOrder = () => {
  const navigate = useNavigate();
  const { orderNumber } = useParams();
  const isEditMode = Boolean(orderNumber);
  const [form] = Form.useForm();
  const [addressForm] = Form.useForm();
  
  // States
  const [loading, setLoading] = useState(false);
  const [orderLoading, setOrderLoading] = useState(isEditMode);
  const [orderData, setOrderData] = useState(null);
  const [myProducts, setMyProducts] = useState([]);
  const [freightChannels, setFreightChannels] = useState([]);
  const [myCustomizations, setMyCustomizations] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [addressFormModalVisible, setAddressFormModalVisible] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [pendingProductSelection, setPendingProductSelection] = useState(null);
  const [productSelectorVisible, setProductSelectorVisible] = useState(false);
  const [productSelectorIndex, setProductSelectorIndex] = useState(null);
  
  // Exchange rates for freight calculation
  const [exchangeRates, setExchangeRates] = useState({});
  
  // 获取国家列表数据
  const countries = countryList();
  const countryOptions = countries.getData();

  useEffect(() => {
    fetchExchangeRates();
    fetchFreightChannels();
    fetchMyCustomizations();

    if (!isEditMode) {
      fetchSavedAddresses();

      form.setFieldsValue({
        order_items: [{
          product_id: undefined,
          variant_path: [],
          selected_variant_key: null,
          quantity: 1,
          freight_id: undefined,
          customization_id: undefined
        }]
      });

      const selectedProductStr = localStorage.getItem('selected_product_for_order');
      if (selectedProductStr) {
        try {
          const selectedProduct = JSON.parse(selectedProductStr);
          localStorage.removeItem('selected_product_for_order');
          setPendingProductSelection(selectedProduct);
          message.success(`Product "${selectedProduct.product_name}" has been pre-selected for your order`);
          // 直接加载预选商品
          loadPendingProduct(selectedProduct);
        } catch (error) {
          
          localStorage.removeItem('selected_product_for_order');
        }
      }
    }
  }, [orderNumber, isEditMode]);

  // 编辑模式下加载订单
  useEffect(() => {
    if (isEditMode && orderNumber) {
      fetchOrderData();
    }
  }, [isEditMode, orderNumber]);

  const fetchOrderData = async () => {
    if (!isEditMode || !orderNumber) return;
    
    try {
      setOrderLoading(true);
      const response = await orderAPI.getByNumber(orderNumber);
      const order = response.data;
      setOrderData(order);
      
      // 获取订单中所有商品的详情
      if (order.items_detail && order.items_detail.length > 0) {
        const productIds = [...new Set(order.items_detail.map(item => item.product_id))];
        const productPromises = productIds.map(id => productAPI.getByIdAuth(id));
        const productResponses = await Promise.all(productPromises);
        
        // 构建商品映射
        const productsMap = {};
        productResponses.forEach(response => {
          const product = response.data;
          productsMap[product.id] = {
            id: product.id,
            product_id: product.id,
            product: product,
            is_active: product.is_active,
            custom_price: null,
            freight_id: null,
            customization_id: null,
          };
        });

        // 更新myProducts，将新加载的商品添加到列表中
        setMyProducts(prevProducts => {
          const existingIds = new Set(prevProducts.map(p => p.product_id));
          const newProducts = Object.values(productsMap).filter(p => !existingIds.has(p.product_id));
          return [...prevProducts, ...newProducts];
        });

        // 初始化表单数据
        form.setFieldsValue({
          customer_name: order.customer_name,
          customer_email: order.customer_email,
          customer_phone: order.customer_phone,
          dest_country: order.dest_country,
          shipping_address: order.shipping_address,
          message: order.message,
          order_items: order.items_detail?.map(item => {
            // 需要根据variant_path找到对应的selected_variant_key
            const product = productsMap[item.product_id];
            let selectedVariantKey = null;
            
            if (product?.product?.variants && Array.isArray(product.product.variants)) {
              const variant = product.product.variants.find(v => 
                JSON.stringify(v.variant_path || []) === JSON.stringify(item.variant_path || [])
              );
              if (variant) {
                selectedVariantKey = variant.id ?? variant.SKU ?? 0;
              }
            }
            
            return {
              product_id: item.product_id,
              variant_path: item.variant_path || [],
              selected_variant_key: selectedVariantKey,
              quantity: item.quantity,
              freight_id: item.freight_id,
              customization_id: item.customization_id || null
            };
          }) || []
        });
        
        // 触发价格预览更新
        setTimeout(() => {
          setRefreshKey(prev => prev + 1);
        }, 100);
      }
      
    } catch (error) {
      
      message.error('Failed to fetch order data');
      navigate('/dropshipper/orders');
    } finally {
      setOrderLoading(false);
    }
  };

  const loadPendingProduct = async (selectedProduct) => {
    if (!selectedProduct || !selectedProduct.product_id) {
      return;
    }

    try {
      // 直接通过API获取商品详情
      const response = await productAPI.getByIdAuth(selectedProduct.product_id);
      const product = response.data;

      // 构建商品对象并添加到myProducts
      const productObj = {
        id: product.id,
        product_id: product.id,
        product: product,
        is_active: product.is_active,
        custom_price: null,
        freight_id: null,
        customization_id: null,
      };

      setMyProducts(prevProducts => {
        const exists = prevProducts.find(p => p.product_id === product.id);
        if (!exists) {
          return [...prevProducts, productObj];
        }
        return prevProducts;
      });

      // 设置表单初始值
      let selectedVariantKey = null;
      if (selectedProduct.variant_path && product.variants && Array.isArray(product.variants)) {
        const variant = product.variants.find(v =>
          JSON.stringify(v.variant_path || []) === JSON.stringify(selectedProduct.variant_path || [])
        );
        if (variant) {
          selectedVariantKey = variant.id ?? variant.SKU ?? 0;
        }
      }

      form.setFieldsValue({
        order_items: [{
          product_id: selectedProduct.product_id,
          variant_path: selectedProduct.variant_path || [],
          selected_variant_key: selectedVariantKey,
          quantity: selectedProduct.quantity || 1,
          freight_id: productObj.freight_id || undefined,
          customization_id: productObj.customization_id || undefined
        }]
      });

      // 触发产品变更逻辑
      setTimeout(() => {
        onProductChange(selectedProduct.product_id, 0, productObj);
        setRefreshKey(prev => prev + 1);
      }, 100);

    } catch (error) {
      
      message.error('Failed to load selected product');
      form.setFieldsValue({
        order_items: [{
          product_id: undefined,
          variant_path: [],
          selected_variant_key: null,
          quantity: 1,
          freight_id: undefined,
          customization_id: undefined
        }]
      });
    } finally {
      setPendingProductSelection(null);
    }
  };

  const fetchExchangeRates = async () => {
    try {
      const response = await exchangeRateAPI.getAll();
      const rates = {};
      response.data.forEach(rate => {
        rates[rate.currency_code] = rate.rate_to_usd;
      });
      setExchangeRates(rates);
    } catch (error) {
      
    }
  };

  const fetchFreightChannels = async () => {
    try {
      const response = await dropshipperAPI.getFreightChannels();
      setFreightChannels(response.data);
    } catch (error) {
      
    }
  };

  const fetchMyCustomizations = async () => {
    try {
      const response = await dropshipperAPI.getMyCustomizations();
      setMyCustomizations(response.data);
    } catch (error) {
      
      setMyCustomizations([]);
    }
  };

  const fetchSavedAddresses = async () => {
    if (isEditMode) return;

    try {
      const response = await addressAPI.getAll();
      setSavedAddresses(response.data);

      if (selectedAddressId) {
        const updatedSelectedAddress = response.data.find(addr => addr.id === selectedAddressId);
        if (updatedSelectedAddress) {
          form.setFieldsValue({
            customer_name: updatedSelectedAddress.customer_name,
            customer_email: updatedSelectedAddress.customer_email,
            customer_phone: updatedSelectedAddress.customer_phone,
            dest_country: updatedSelectedAddress.country,
            shipping_address: updatedSelectedAddress.address,
          });
          setRefreshKey(prev => prev + 1);
          setTimeout(() => {
            onCountryChange(updatedSelectedAddress.country);
          }, 100);
        }
      } else {
        const defaultAddress = response.data.find(addr => addr.is_default);
        if (defaultAddress) {
          setSelectedAddressId(defaultAddress.id);
          // Ensure form fields align with default address
          form.setFieldsValue({
            customer_name: defaultAddress.customer_name,
            customer_email: defaultAddress.customer_email,
            customer_phone: defaultAddress.customer_phone,
            dest_country: defaultAddress.country,
            shipping_address: defaultAddress.address,
          });
          setRefreshKey(prev => prev + 1);
          setTimeout(() => {
            onCountryChange(defaultAddress.country);
          }, 100);
        } else {
          setSelectedAddressId(null);
          form.setFieldsValue({
            customer_name: undefined,
            customer_email: undefined,
            customer_phone: undefined,
            dest_country: undefined,
            shipping_address: undefined,
          });
          setRefreshKey(prev => prev + 1);
        }
      }
    } catch (error) {
      
    }
  };

  const handleAddressSelect = (addressId) => {
    if (isEditMode) return;

    setSelectedAddressId(addressId);
    const selectedAddress = savedAddresses.find(addr => addr.id === addressId);
    if (selectedAddress) {
      form.setFieldsValue({
        customer_name: selectedAddress.customer_name,
        customer_email: selectedAddress.customer_email,
        customer_phone: selectedAddress.customer_phone,
        dest_country: selectedAddress.country,
        shipping_address: selectedAddress.address,
      });
      setRefreshKey(prev => prev + 1);
      setTimeout(() => {
        onCountryChange(selectedAddress.country);
      }, 100);
    } else {
      form.setFieldsValue({
        customer_name: undefined,
        customer_email: undefined,
        customer_phone: undefined,
        dest_country: undefined,
        shipping_address: undefined,
      });
      setRefreshKey(prev => prev + 1);
    }
  };

  const handleNewAddress = () => {
    if (isEditMode) return;
    setEditingAddress(null);
    addressForm.resetFields();
    setAddressFormModalVisible(true);
  };

  const handleEditAddress = () => {
    if (isEditMode) return;
    if (selectedAddressId) {
      const address = savedAddresses.find(addr => addr.id === selectedAddressId);
      if (address) {
        setEditingAddress(address);
        addressForm.setFieldsValue(address);
        setAddressFormModalVisible(true);
      }
    }
  };

  const handleAddressSubmit = async (values) => {
    if (isEditMode) return;

    try {
      if (editingAddress) {
        await addressAPI.update(editingAddress.id, values);
        message.success('Address updated successfully');

        if (selectedAddressId === editingAddress.id) {
          form.setFieldsValue({
            customer_name: values.customer_name,
            customer_email: values.customer_email,
            customer_phone: values.customer_phone,
            dest_country: values.country,
            shipping_address: values.address,
          });
          setRefreshKey(prev => prev + 1);
          setTimeout(() => {
            onCountryChange(values.country);
          }, 100);
        }
      } else {
        await addressAPI.create(values);
        message.success('Address created successfully');
      }

      setAddressFormModalVisible(false);
      addressForm.resetFields();
      fetchSavedAddresses();
    } catch (error) {
      message.error('Failed to save address');
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      if (isEditMode) {
        const updateData = {
          ...values,
          order_items: (values.order_items || []).map(item => ({
            product_id: item.product_id,
            quantity: item.quantity,
            variant_path: item.variant_path,
            freight_id: item.freight_id,
            customization_id: item.customization_id || null
          }))
        };

        await orderAPI.edit(orderNumber, updateData);
        message.success('Order updated successfully');
      } else {
        if (!selectedAddressId) {
          message.error('Please select a saved address');
          return;
        }

        const selectedAddress = savedAddresses.find(addr => addr.id === selectedAddressId);
        if (!selectedAddress) {
          message.error('Selected address not found');
          return;
        }

        const orderData = {
          customer_name: selectedAddress.customer_name,
          customer_email: selectedAddress.customer_email || null,
          customer_phone: selectedAddress.customer_phone,
          shipping_address: selectedAddress.address,
          dest_country: selectedAddress.country,
          message: values.message || null,
          order_items: (values.order_items || []).map(item => ({
            product_id: item.product_id,
            quantity: item.quantity,
            variant_path: item.variant_path || null,
            freight_id: item.freight_id,
            customization_id: item.customization_id || null
          }))
        };

        await orderAPI.create(orderData);
        message.success('Order created successfully');
      }

      navigate('/dropshipper/orders');
    } catch (error) {
      if (isEditMode) {
        if (error.response?.data?.detail) {
          message.error(error.response.data.detail);
        } else {
          message.error('Failed to update order');
        }
      } else if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          message.error(detail);
        } else if (Array.isArray(detail)) {
          const errorMessages = detail.map(err => {
            if (typeof err === 'string') return err;
            if (err.msg) return err.msg;
            if (err.message) return err.message;
            return JSON.stringify(err);
          });
          message.error(`Validation errors: ${errorMessages.join(', ')}`);
        } else if (typeof detail === 'object') {
          message.error(detail.message || detail.msg || 'Invalid input data');
        } else {
          message.error('Failed to create order');
        }
      } else {
        message.error('Failed to create order');
      }
    } finally {
      setLoading(false);
    }
  };

  const onProductChange = (productId, index, suppliedProductEntry = null) => {
    const productEntry = suppliedProductEntry || myProducts.find(p => p.product_id === productId);
    if (!productEntry) {
      return;
    }

    const productData = productEntry.product;
    const orderItems = form.getFieldValue('order_items');

    // 检查是否为单品（无变体）
    const isSimple = !productData?.variant_types || productData.variant_types.length === 0;

    // 检查当前item是否已经有预选的变体（从loadPendingProduct来的）
    const hasPreSelectedVariant = orderItems[index]?.selected_variant_key !== null && 
                                  orderItems[index]?.selected_variant_key !== undefined &&
                                  orderItems[index]?.variant_path !== undefined;

    if (isSimple) {
      // 单品情况，自动设置variant_path为空数组和selected_variant_key
      orderItems[index].variant_path = [];
      const variant = productData?.variants?.[0];
      if (variant) {
        orderItems[index].selected_variant_key = variant.id ?? variant.SKU ?? 0;
      }
      // 计算单品价格
      const price = productEntry.custom_price || variant?.price || 0;
      orderItems[index].unit_price = price;
    } else if (!hasPreSelectedVariant) {
      // 多变体情况，仅在没有预选变体时重置变体选择
      orderItems[index].variant_path = undefined;
      orderItems[index].selected_variant_key = undefined;
      orderItems[index].unit_price = 0;
    } else {
      // 有预选变体，保持不变，只更新价格
      const variantPath = orderItems[index].variant_path;
      if (variantPath && Array.isArray(variantPath)) {
        const variant = productData.variants?.find(v =>
          JSON.stringify(v.variant_path || []) === JSON.stringify(variantPath)
        );
        if (variant) {
          const price = productEntry.custom_price || variant.price || 0;
          orderItems[index].unit_price = price;
          orderItems[index].weight = variant.weight;
          orderItems[index].SKU = variant.SKU;
        }
      }
    }

    // 自动设置该产品对应的freight和customization
    if (productEntry.freight_id) {
      orderItems[index].freight_id = productEntry.freight_id;
    }
    if (productEntry.customization_id) {
      orderItems[index].customization_id = productEntry.customization_id;
    }

    form.setFieldsValue({ order_items: orderItems });

    // 对于单品，自动选择第一个variant
    if (isSimple && productData?.variants && Array.isArray(productData.variants) && productData.variants.length > 0) {
      const firstVariant = productData.variants[0];
      const opts = getVariantOptions(productData);
      const picked = opts.find(o => o.value === (firstVariant.id ?? firstVariant.SKU ?? 0));

      if (picked) {
        setTimeout(() => {
          form.setFieldsValue({
            order_items: orderItems.map((it, i) =>
              i === index
                ? {
                    ...it,
                    selected_variant_key: picked.value,
                    variant_path: picked.data.variant_path || [],
                    unit_price: productEntry.custom_price || picked.data.price || 0,
                    weight: picked.data.weight ?? it.weight,
                    SKU: picked.data.SKU ?? it.SKU,
                  }
                : it
            )
          });
        }, 50);
      }
    }

    // 触发价格预览更新
    setTimeout(() => {
      setRefreshKey(prev => prev + 1);
    }, 100);
  };

  const openProductSelector = (fieldIndex) => {
    setProductSelectorIndex(fieldIndex);
    setProductSelectorVisible(true);
  };

  const closeProductSelector = () => {
    setProductSelectorVisible(false);
    setProductSelectorIndex(null);
  };

  const handleProductSelected = (productId, productData) => {
    if (productSelectorIndex === null) {
      closeProductSelector();
      return;
    }

    const productEntry = {
      id: productData.id,
      product_id: productData.id,
      product: productData,
      is_active: productData.is_active,
      custom_price: null,
      freight_id: null,
      customization_id: null,
    };

    setMyProducts(prev => {
      const existingIndex = prev.findIndex(p => p.product_id === productId);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          ...productEntry,
        };
        return updated;
      }
      return [...prev, productEntry];
    });

    const orderItems = form.getFieldValue('order_items') || [];
    const currentItem = { ...(orderItems[productSelectorIndex] || {}) };
    currentItem.product_id = productId;
    orderItems[productSelectorIndex] = currentItem;
    form.setFieldsValue({ order_items: orderItems });
    form.validateFields([['order_items', productSelectorIndex, 'product_id']]).catch(() => {});

    setTimeout(() => {
      onProductChange(productId, productSelectorIndex, productEntry);
    }, 0);

    closeProductSelector();
  };

  // 获取产品的变体选项
  const getVariantOptions = (product) => {
    if (!product || !product.variants) return [];

    // 新的扁平化格式：判断是否为单品
    const isSimple = !product.variant_types || product.variant_types.length === 0;
    
    if (isSimple) {
      // 单品：variants是数组，取第一个元素
      if (Array.isArray(product.variants) && product.variants.length > 0) {
        const variant = product.variants[0];
        return [{ 
          value: variant.id ?? variant.SKU ?? 0, // 用 id 或 SKU 作稳定值
          label: 'Single Product', 
          data: variant 
        }];
      }
      return [];
    }

    // 多变体：variants是数组，每个元素有variant_path
    if (Array.isArray(product.variants)) {
      return product.variants.map((variant, index) => ({
        value: variant.id ?? variant.SKU ?? index, // 首选 id，其次 SKU，最后才退回 index
        label: (variant.variant_path || []).join(' - '),
        data: variant
      }));
    }

    return [];
  };

  // 获取变体信息的辅助函数
  const getVariantInfo = (variants, variantPath) => {
    if (!variants || !Array.isArray(variants)) return null;
    
    if (!variantPath || variantPath.length === 0) {
      // 单品情况，查找没有variant_path或variant_path为空的项
      for (const variant of variants) {
        if (!variant.variant_path || variant.variant_path.length === 0) {
          return variant;
        }
      }
      return null;
    } else {
      // 多变体情况，根据variant_path查找匹配的项
      for (const variant of variants) {
        const variantVariantPath = variant.variant_path || [];
        if (JSON.stringify(variantVariantPath) === JSON.stringify(variantPath)) {
          return variant;
        }
      }
      return null;
    }
  };

  // 获取产品封面图片URL
  const getProductCoverImage = (product) => {
    if (!product?.images?.length) {
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjEwIiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+Tm8gSW1hZ2U8L3RleHQ+PC9zdmc+';
    }
    
    // 优先显示非变体图片作为封面
    const coverImage = product.images.find(img => !img.for_variants) || product.images[0];
    return `${API_BASE_URL}/products/${product.id}/images/${coverImage.id}`;
  };

  // 获取变体图片URL
  const getVariantImage = (product, variantPath) => {
    if (!product?.images?.length || !variantPath || variantPath.length === 0) return null;
    
    const variantInfo = getVariantInfo(product.variants, variantPath);
    if (!variantInfo?.image) return null;
    
    return `${API_BASE_URL}/products/${product.id}/images/${variantInfo.image}`;
  };

  // 货币转换辅助函数
  const convertToUSD = (amount, currency) => {
    if (currency === 'USD') {
      return amount;
    }
    
    if (exchangeRates[currency]) {
      // rate_to_usd 表示 1 USD = X 目标货币，所以转换为USD需要除以这个值
      const usdRate = 1.0 / exchangeRates[currency];
      return amount * usdRate;
    }
    
    return amount; // 如果没有汇率信息，返回原值
  };

  // 新增的辅助函数
  const onCountryChange = (countryCode) => {
    // Force re-render to update freight validation status for all items
    setRefreshKey(prev => prev + 1);
    
    // Also trigger validation for all order items
    const orderItems = form.getFieldValue('order_items') || [];
    orderItems.forEach((item, index) => {
      if (item.freight_id) {
        // Re-calculate freight validation for each item
        setTimeout(() => {
          onFreightChange(item.freight_id, index);
        }, 50);
      }
    });
  };

  const onFreightChange = (freightId, itemIndex) => {
    const orderItems = form.getFieldValue('order_items') || [];
    const destCountry = form.getFieldValue('dest_country');
    
    if (freightId && destCountry) {
      const freight = freightChannels.find(f => f.id === freightId);
      if (freight && freight.data[destCountry]) {
        // Freight is available for this country
        calculateItemPrice(itemIndex);
      } else {
        // Don't show popup message, let the red text below handle the error display
        calculateItemPrice(itemIndex);
      }
    } else {
      calculateItemPrice(itemIndex);
    }
    
    // 触发价格预览更新
    setTimeout(() => {
      setRefreshKey(prev => prev + 1);
    }, 100);
  };

  const calculateItemPrice = (itemIndex) => {
    // This is just for display purposes - actual calculation is done in backend
    const orderItems = form.getFieldValue('order_items') || [];
    const destCountry = form.getFieldValue('dest_country');
    const item = orderItems[itemIndex];
    
    if (!item || !item.product_id || (item.variant_path === undefined)) return null;
    
    // Get product price
    const product = myProducts.find(p => p.product_id === item.product_id);
    let itemPrice = 0;
    
    if (product?.product?.variants && Array.isArray(product.product.variants)) {
      // 根据variant_path找到对应的variant
      const variant = product.product.variants.find(v => 
        JSON.stringify(v.variant_path || []) === JSON.stringify(item.variant_path || [])
      );
      
      // 优先使用dropshipper的自定义价格，然后使用variant价格
      if (product?.custom_price !== undefined && product?.custom_price !== null) {
        itemPrice = product.custom_price;
      } else {
        itemPrice = variant?.price || 0;
      }
    }
    
    // Get customization price
    let customizationPrice = 0;
    if (item.customization_id) {
      const customization = myCustomizations.find(c => c.id === item.customization_id);
      customizationPrice = customization?.price_prepacket || 0;
    }
    
    // Calculate freight price (accurate calculation based on weight)
    let freightPrice = 0;
    if (item.freight_id && destCountry) {
      const freight = freightChannels.find(f => f.id === item.freight_id);
      if (freight && freight.data[destCountry]) {
        const countryData = freight.data[destCountry];
        if (countryData.weight) {
          // 计算总重量
          const variant = product?.product?.variants?.find(v => 
            JSON.stringify(v.variant_path || []) === JSON.stringify(item.variant_path || [])
          );
          const itemWeight = variant?.weight || 100; // 默认100g
          const totalWeight = itemWeight * (item.quantity || 1);
          
          // 找到适合的重量段（找最小的满足条件的重量段）
          let suitableRange = null;
          for (const [weightStr, pricing] of Object.entries(countryData.weight)) {
            const weightLimit = parseInt(weightStr);
            if (totalWeight <= weightLimit) {
              if (!suitableRange || weightLimit < parseInt(suitableRange[0])) {
                suitableRange = [weightStr, pricing];
              }
            }
          }
          
          if (suitableRange) {
            const pricing = suitableRange[1];
            const prePacket = parseFloat(pricing.pre_packet || 0);
            const preKg = parseFloat(pricing.pre_kg || 0);
            const currency = pricing.currency || 'USD';
            
            // 计算运费：每包裹费用 + 每公斤费用 * 重量(kg)
            const weightKg = totalWeight / 1000.0;
            const originalFreightPrice = prePacket + (preKg * weightKg);
            
            // 转换为USD
            freightPrice = convertToUSD(originalFreightPrice, currency);
          }
        }
      }
    }
    
    const quantity = item.quantity || 1;
    
    return {
      itemPrice: itemPrice * quantity,
      customizationPrice: customizationPrice * quantity,
      freightPrice: freightPrice,
      total: (itemPrice * quantity) + (customizationPrice * quantity) + freightPrice
    };
  };

  const renderPricePreview = (itemIndex) => {
    const prices = calculateItemPrice(itemIndex);
    if (prices) {
      return `Item: $${normalizeMoney(prices.itemPrice)} | Customization: $${normalizeMoney(prices.customizationPrice)} | Freight: $${normalizeMoney(prices.freightPrice)} | Total: $${normalizeMoney(prices.total)}`;
    }
    return 'Item: $0.00 | Customization: $0.00 | Freight: $0.00 | Total: $0.00';
  };

  const getFreightStatus = (itemIndex) => {
    const orderItems = form.getFieldValue('order_items') || [];
    const destCountry = form.getFieldValue('dest_country');
    const item = orderItems[itemIndex];
    
    if (item?.freight_id && destCountry) {
      const freight = freightChannels.find(f => f.id === item.freight_id);
      if (freight && !freight.data[destCountry]) {
        return 'error';
      }
    }
    return undefined;
  };

  const getFreightError = (itemIndex) => {
    const orderItems = form.getFieldValue('order_items') || [];
    const destCountry = form.getFieldValue('dest_country');
    const item = orderItems[itemIndex];
    
    if (item?.freight_id && destCountry) {
      const freight = freightChannels.find(f => f.id === item.freight_id);
      if (freight && !freight.data[destCountry]) {
        const countryName = countryOptions.find(c => c.value === destCountry)?.label || destCountry;
        return `Cannot ship to ${countryName} with ${freight.name}`;
      }
    }
    return null;
  };

  if (isEditMode && orderLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (isEditMode && !orderLoading && !orderData) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px' 
      }}>
        <div>Order not found</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '16px' }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate('/dropshipper/orders')}
          size="large"
        >
          Back to Orders
        </Button>
        <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 'bold' }}>
          {isEditMode && orderData
            ? `Edit Order #${orderData.order_number}`
            : 'Create New Order'}
        </h1>
        {isEditMode && orderData && (
          <div style={{ 
            padding: '4px 12px', 
            backgroundColor: '#f0f8ff', 
            borderRadius: '4px',
            fontSize: '12px',
            color: '#1890ff'
          }}>
            Status: {orderData.status}
          </div>
        )}
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Row gutter={32}>
          {/* Left Column - Address and Message */}
          <Col span={10}>
            <Card title="Shipping Address" style={{ marginBottom: '24px' }}>
              {isEditMode ? (
                <>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="Customer Name"
                        name="customer_name"
                        rules={[{ required: true, message: 'Please enter customer name' }]}
                      >
                        <Input placeholder="Enter customer name" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Customer Phone"
                        name="customer_phone"
                        rules={[{ required: true, message: 'Please enter customer phone' }]}
                      >
                        <Input placeholder="Enter customer phone" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    label="Customer Email (Optional)"
                    name="customer_email"
                    rules={[
                      { type: 'email', message: 'Please enter valid email address' }
                    ]}
                  >
                    <Input placeholder="Enter email address (optional)" />
                  </Form.Item>

                  <Form.Item
                    label="Country/Region"
                    name="dest_country"
                    rules={[{ required: true, message: 'Please select country/region' }]}
                  >
                    <Select
                      placeholder="Select country/region"
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      onChange={onCountryChange}
                    >
                      {countryOptions.map(country => (
                        <Option key={country.value} value={country.value} label={country.label}>
                          {country.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    label="Detailed Address"
                    name="shipping_address"
                    rules={[{ required: true, message: 'Please enter detailed address' }]}
                  >
                    <Input.TextArea 
                      placeholder="Enter detailed address" 
                      rows={3}
                    />
                  </Form.Item>
                </>
              ) : (
                <>
                  <Form.Item label="Select Saved Address">
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Select
                        placeholder="Select a saved address"
                        value={selectedAddressId}
                        onChange={handleAddressSelect}
                        style={{ flex: 1 }}
                        allowClear
                      >
                        {savedAddresses.map(address => (
                          <Option key={address.id} value={address.id}>
                            {address.name} - {countryOptions.find(c => c.value === address.country)?.label || address.country} - {address.address.substring(0, 50)}...
                          </Option>
                        ))}
                      </Select>
                      {selectedAddressId && (
                        <Button onClick={handleEditAddress}>
                          Edit
                        </Button>
                      )}
                      <Button onClick={handleNewAddress}>
                        + New
                      </Button>
                    </div>
                  </Form.Item>

                  {selectedAddressId && (() => {
                    const selectedAddress = savedAddresses.find(addr => addr.id === selectedAddressId);
                    return selectedAddress ? (
                      <Card size="small" style={{ backgroundColor: '#f5f5f5' }}>
                        <Descriptions size="small" column={1}>
                          <Descriptions.Item label="Name">{selectedAddress.customer_name}</Descriptions.Item>
                          <Descriptions.Item label="Phone">{selectedAddress.customer_phone}</Descriptions.Item>
                          <Descriptions.Item label="Email">{selectedAddress.customer_email || 'Not provided'}</Descriptions.Item>
                          <Descriptions.Item label="Country">{countryOptions.find(c => c.value === selectedAddress.country)?.label || selectedAddress.country}</Descriptions.Item>
                          <Descriptions.Item label="Address">{selectedAddress.address}</Descriptions.Item>
                        </Descriptions>
                      </Card>
                    ) : null;
                  })()}

                  {selectedAddressId && (
                    <>
                      <Form.Item name="customer_name" style={{ display: 'none' }}>
                        <Input />
                      </Form.Item>
                      <Form.Item name="customer_email" style={{ display: 'none' }}>
                        <Input />
                      </Form.Item>
                      <Form.Item name="customer_phone" style={{ display: 'none' }}>
                        <Input />
                      </Form.Item>
                      <Form.Item name="dest_country" style={{ display: 'none' }}>
                        <Input />
                      </Form.Item>
                      <Form.Item name="shipping_address" style={{ display: 'none' }}>
                        <Input />
                      </Form.Item>
                    </>
                  )}

                  {!selectedAddressId && (
                    <div style={{ 
                      padding: '20px', 
                      textAlign: 'center', 
                      backgroundColor: '#fff', 
                      border: '1px dashed #d9d9d9',
                      borderRadius: '6px'
                    }}>
                      <p style={{ margin: 0, color: '#666' }}>
                        Please select a saved address above or click "+ New" to create one.
                      </p>
                    </div>
                  )}
                </>
              )}
            </Card>

            <Card title="Order Message">
              <Form.Item
                label="Order Message (Optional)"
                name="message"
              >
                <TextArea 
                  placeholder="Enter any special instructions or notes for this order..."
                  rows={4}
                  maxLength={1000}
                  showCount
                />
              </Form.Item>
            </Card>
          </Col>

          {/* Right Column - Order Items */}
          <Col span={14}>
            <Card title="Order Items">
              <Form.List name="order_items">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <Card key={`${key}-${refreshKey}`} size="small" style={{ marginBottom: 16 }}>
                        {/* Product and Variant Selection */}
                        <Row gutter={16}>
                          <Col span={10}>
                            <Form.Item
                              {...restField}
                              name={[name, 'product_id']}
                              label="Product"
                              rules={[{ required: true, message: 'Please select product' }]}
                            >
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <Input type="hidden" />
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                  <Button type="dashed" onClick={() => openProductSelector(name)}>
                                    Select Product
                                  </Button>
                                  {(() => {
                                    const orderItems = form.getFieldValue('order_items') || [];
                                    const productId = orderItems[name]?.product_id;
                                    if (!productId) {
                                      return <span style={{ color: '#999' }}>No product selected</span>;
                                    }
                                    const productEntry = myProducts.find(p => p.product_id === productId);
                                    const productName = productEntry?.product?.name || productId;
                                    return (
                                      <span style={{ color: '#555' }}>
                                        {productName}
                                      </span>
                                    );
                                  })()}
                                </div>
                              </div>
                            </Form.Item>
                          </Col>
                          <Col span={2}>
                            {/* Product Cover Image */}
                            {(() => {
                              const orderItems = form.getFieldValue('order_items') || [];
                              const productId = orderItems[name]?.product_id;
                              const product = myProducts.find(p => p.product_id === productId);
                              const productData = product?.product;
                              
                              if (productData) {
                                const coverImageUrl = getProductCoverImage(productData);
                                return (
                                  <div style={{ marginTop: '30px' }}>
                                    <img
                                      src={coverImageUrl}
                                      alt="Product cover"
                                      width={32}
                                      height={32}
                                      style={{ 
                                        objectFit: 'cover', 
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                      }}
                                      onError={(e) => {
                                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjgiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5OL0E8L3RleHQ+PC9zdmc+'
                                      }}
                                    />
                                  </div>
                                );
                              }
                              return null;
                            })()}
                          </Col>
                          <Col span={10}>
                            <Form.Item
                              {...restField}
                              name={[name, 'selected_variant_key']}
                              label="Variant"
                              rules={[
                                { 
                                  validator: (_, value) => {
                                    const orderItems = form.getFieldValue('order_items') || [];
                                    const productId = orderItems[name]?.product_id;
                                    const product = myProducts.find(p => p.product_id === productId);
                                    
                                    if (!product) {
                                      return Promise.reject(new Error('Please select a product first'));
                                    }
                                    
                                    // For single product (no variants), empty array is valid
                                    const isSimple = !product.product?.variant_types || product.product.variant_types.length === 0;
                                    if (isSimple) {
                                      return Promise.resolve();
                                    }
                                    
                                    // For multi-variant products, value must not be empty
                                    if (value === undefined || value === null) {
                                      return Promise.reject(new Error('Please select variant'));
                                    }
                                    
                                    return Promise.resolve();
                                  }
                                }
                              ]}
                            >
                              <Select 
                                placeholder="Select variant"
                                optionLabelProp="children"
                                onChange={(selectedKey) => {
                                  const orderItems = form.getFieldValue('order_items') || [];
                                  const productId = orderItems[name]?.product_id;
                                  const product = myProducts.find(p => p.product_id === productId)?.product;

                                  if (!product) return;

                                  const opts = getVariantOptions(product);
                                  const picked = opts.find(o => o.value === selectedKey);
                                  if (!picked) return;

                                  // 选中后，分别写回业务字段
                                  form.setFieldsValue({
                                    order_items: orderItems.map((it, i) =>
                                      i === name
                                        ? {
                                            ...it,
                                            variant_path: picked.data.variant_path || [],
                                            unit_price: product.custom_price || picked.data.price || 0,
                                            weight: picked.data.weight ?? it.weight,
                                            SKU: picked.data.SKU ?? it.SKU,
                                          }
                                        : it
                                    )
                                  });

                                  // 触发价格预览更新
                                  setTimeout(() => {
                                    setRefreshKey(prev => prev + 1);
                                  }, 100);
                                }}
                                disabled={!form.getFieldValue(['order_items', name, 'product_id'])}
                              >
                                {(() => {
                                  const orderItems = form.getFieldValue('order_items') || [];
                                  const productId = orderItems[name]?.product_id;
                                  const product = myProducts.find(p => p.product_id === productId)?.product;

                                  return getVariantOptions(product).map(option => (
                                    <Option key={option.value} value={option.value}>
                                    {`${option.label} - $${normalizeMoney(option.data.price ?? '0')} - SKU: ${option.data.SKU || 'N/A'}`}
                                    </Option>
                                  ));
                                })()}
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col span={2}>
                            {/* Variant Image */}
                            {(() => {
                              const orderItems = form.getFieldValue('order_items') || [];
                              const productId = orderItems[name]?.product_id;
                              const variantPath = orderItems[name]?.variant_path;
                              const product = myProducts.find(p => p.product_id === productId);
                              const productData = product?.product;
                              
                              if (productData && variantPath !== undefined && Array.isArray(variantPath) && variantPath.length > 0) {
                                const variantImageUrl = getVariantImage(productData, variantPath);
                                if (variantImageUrl) {
                                  return (
                                    <div style={{ marginTop: '30px' }}>
                                      <img
                                        src={variantImageUrl}
                                        alt="Variant image"
                                        width={32}
                                        height={32}
                                        style={{ 
                                          objectFit: 'cover', 
                                          borderRadius: '4px',
                                          border: '2px solid #2557ff',
                                          cursor: 'pointer'
                                        }}
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                        }}
                                      />
                                    </div>
                                  );
                                }
                              }
                              return null;
                            })()}
                          </Col>
                        </Row>
                        
                        {/* Freight and Customization Selection */}
                        <Row gutter={16}>
                          <Col span={8}>
                            <Form.Item
                              {...restField}
                              name={[name, 'freight_id']}
                              label="Freight Channel"
                              rules={[{ required: true, message: 'Please select freight channel' }]}
                            >
                              <Select 
                                placeholder="Select freight channel"
                                onChange={(value) => onFreightChange(value, name)}
                                status={getFreightStatus(name)}
                              >
                                {(() => {
                                  // 获取当前选中的产品
                                  const orderItems = form.getFieldValue('order_items') || [];
                                  const productId = orderItems[name]?.product_id;
                                  const product = myProducts.find(p => p.product_id === productId);
                                  
                                  // 只显示商品支持的物流渠道
                                  const supportedChannelIds = product?.product?.supported_freight_channels || [];
                                  const availableChannels = freightChannels.filter(freight => 
                                    supportedChannelIds.includes(freight.id)
                                  );
                                  
                                  return availableChannels.map(freight => (
                                    <Option key={freight.id} value={freight.id}>
                                      {freight.name}
                                    </Option>
                                  ));
                                })()}
                              </Select>
                            </Form.Item>
                            {getFreightError(name) && (
                              <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '-16px', marginBottom: '8px' }}>
                                {getFreightError(name)}
                              </div>
                            )}
                            {/* 显示商品支持的渠道数量提示 */}
                            {(() => {
                              const orderItems = form.getFieldValue('order_items') || [];
                              const productId = orderItems[name]?.product_id;
                              const product = myProducts.find(p => p.product_id === productId);
                              const supportedChannelIds = product?.product?.supported_freight_channels || [];
                              
                              if (productId && supportedChannelIds.length === 0) {
                                return (
                                  <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '-8px', marginBottom: '8px' }}>
                                    This product doesn't support any freight channels. Please contact admin.
                                  </div>
                                );
                              } 
                              return null;
                            })()}
                          </Col>
                          <Col span={8}>
                            <Form.Item
                              {...restField}
                              name={[name, 'customization_id']}
                              label="Customization (Optional)"
                            >
                              <Select 
                                placeholder="Select customization"
                                allowClear
                                onChange={() => {
                                  calculateItemPrice(name);
                                  setTimeout(() => setRefreshKey(prev => prev + 1), 100);
                                }}
                              >
                                {myCustomizations.map(customization => (
                                  <Option key={customization.id} value={customization.id}>
                                  {customization.customization_name} - {normalizeMoney(customization.price_prepacket ?? '0')}/pkg
                                  </Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item
                              {...restField}
                              name={[name, 'quantity']}
                              label="Quantity"
                              rules={[{ required: true, message: 'Please enter quantity' }]}
                            >
                              <InputNumber 
                                min={1} 
                                style={{ width: '100%' }}
                                onChange={() => {
                                  calculateItemPrice(name);
                                  setTimeout(() => setRefreshKey(prev => prev + 1), 100);
                                }}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                        
                        {/* Price Preview and Remove Button */}
                        <Row gutter={16} align="middle">
                          <Col span={20}>
                            <div style={{ 
                              padding: '8px 12px', 
                              backgroundColor: '#f5f5f5', 
                              borderRadius: '4px',
                              fontSize: '12px'
                            }}>
                              <strong>Price Preview:</strong> {renderPricePreview(name)}
                            </div>
                          </Col>
                          <Col span={4}>
                            <Button 
                              type="text" 
                              danger 
                              onClick={() => remove(name)}
                              disabled={fields.length === 1}
                              block
                            >
                              Remove
                            </Button>
                          </Col>
                        </Row>
                      </Card>
                    ))}
                    <Form.Item>
                      <Button 
                        type="dashed" 
                        onClick={() => add({ 
                          product_id: undefined, 
                          variant_path: undefined, 
                          selected_variant_key: undefined,
                          quantity: 1, 
                          freight_id: undefined,
                          customization_id: undefined
                        })} 
                        block 
                        icon={<PlusOutlined />}
                      >
                        Add Order Item
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>
            </Card>

            {/* Action Buttons */}
            <div style={{ marginTop: '24px', textAlign: 'right' }}>
              <Button 
                size="large" 
                onClick={() => navigate('/dropshipper/orders')}
                style={{ marginRight: '12px' }}
              >
                Cancel
              </Button>
              <Button 
                type="primary" 
                size="large"
                htmlType="submit"
                loading={loading}
                style={{ backgroundColor: '#ff8c00', borderColor: '#ff8c00' }}
              >
                {isEditMode ? 'Update Order' : 'Create Order'}
              </Button>
            </div>
          </Col>
        </Row>
      </Form>

      <Modal
        title="Select Product"
        open={productSelectorVisible}
        onCancel={closeProductSelector}
        footer={null}
        width={780}
        destroyOnClose
      >
        <ProductSearchSelector
          multiple={false}
          onSelect={handleProductSelected}
        />
      </Modal>

      {!isEditMode && (
        <Modal
          title={editingAddress ? 'Edit Address' : 'Add New Address'}
          open={addressFormModalVisible}
          onCancel={() => {
            setAddressFormModalVisible(false);
            addressForm.resetFields();
            setEditingAddress(null);
          }}
          onOk={() => addressForm.submit()}
          okText={editingAddress ? 'Update' : 'Create'}
          okButtonProps={{ style: { backgroundColor: '#ff8c00', borderColor: '#ff8c00' } }}
        >
          <Form
            form={addressForm}
            layout="vertical"
            onFinish={handleAddressSubmit}
          >
            <Form.Item
              label="Address Name/Label"
              name="name"
              rules={[{ required: true, message: 'Please enter address name' }]}
            >
              <Input placeholder="e.g., Home, Office, Warehouse" />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Customer Name"
                  name="customer_name"
                  rules={[{ required: true, message: 'Please enter customer name' }]}
                >
                  <Input placeholder="Enter customer name" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Customer Phone"
                  name="customer_phone"
                  rules={[{ required: true, message: 'Please enter customer phone' }]}
                >
                  <Input placeholder="Enter customer phone" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              label="Customer Email (Optional)"
              name="customer_email"
              rules={[
                { type: 'email', message: 'Please enter valid email address' }
              ]}
            >
              <Input placeholder="Enter email address (optional)" />
            </Form.Item>

            <Form.Item
              label="Country/Region"
              name="country"
              rules={[{ required: true, message: 'Please select country/region' }]}
            >
              <Select
                placeholder="Select country/region"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {countryOptions.map(country => (
                  <Option key={country.value} value={country.value} label={country.label}>
                    {country.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Detailed Address"
              name="address"
              rules={[{ required: true, message: 'Please enter detailed address' }]}
            >
              <Input.TextArea 
                placeholder="Enter detailed address" 
                rows={3}
              />
            </Form.Item>
          </Form>
        </Modal>
      )}
    </div>
  );
};

export default EditOrder;
